import React, { useState } from 'react';

import { HelpCircle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { HELP_CENTER_URL } from 'shared/constants';
import { useLangNavigate } from 'shared/hooks';

import { HomeFAQ } from './HomeFAQ';
import { CallIcon, CheckIcon, EmailIcon, KakaoIcon } from './Icons';

type TabType = 'booking' | 'reservation' | 'moreHelp' | 'settling';

function FaqSection() {
  const { t } = useTranslation('home');
  const { navigateWithLang } = useLangNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('booking');

  const TABS = [
    { id: 'booking' as TabType, labelKey: 'faq.tabs.booking', gaLabel: 'booking' },
    { id: 'reservation' as TabType, labelKey: 'faq.tabs.reservation', gaLabel: 'reservation_moving_in' },
    { id: 'moreHelp' as TabType, labelKey: 'faq.tabs.moreHelp', gaLabel: 'more_help' },
    { id: 'settling' as TabType, labelKey: 'faq.tabs.settling', gaLabel: 'settling_in' },
  ];

  const CONTACT_CHANNELS = [
    {
      href: 'mailto:official@checkmatekorea.com',
      label: t('faq.channels.email'),
      icon: <EmailIcon />,
      external: false,
      gaLabel: 'email',
    },
    {
      href: 'http://pf.kakao.com/_xjSxcxgxj',
      label: t('faq.channels.kakao'),
      icon: <KakaoIcon />,
      external: true,
      gaLabel: 'kakao',
    },
    {
      href: 'https://api.whatsapp.com/send/?phone=821087033395&text&type=phone_number&app_absent=0',
      label: t('faq.channels.whatsapp'),
      icon: <CallIcon />,
      external: true,
      gaLabel: 'whatsapp',
    },
    {
      href: HELP_CENTER_URL,
      label: t('faq.channels.helpCenter'),
      icon: <HelpCircle className="h-6 w-6 text-white" strokeWidth={1.8} />,
      external: true,
      gaLabel: 'help_center',
    },
  ];

  return (
    <section className="relative mx-auto w-full bg-[#251F1F] pb-10 pt-8 md:pb-[140px] md:pt-0">
      <div className="mx-auto flex w-full max-w-[1154px] flex-col gap-6 px-5 md:flex-row md:gap-8 md:gap-x-[50px] md:gap-y-0 md:px-6">
        {/* Left Side - Help Info */}
        <div className="flex flex-col gap-6 md:gap-y-[86px] md:pt-[140px]">
          <div className="flex flex-row items-center gap-3 md:flex-col md:items-start md:gap-[24px]">
            <img
              src="/images/optimized/illusts/il_bookingguide_faq.webp"
              alt="faq"
              className="h-12 w-12 shrink-0 object-cover md:h-[72px] md:w-[72px]"
            />
            <div className="flex flex-col gap-1 md:gap-0">
              <h2 className="text-[18px] font-bold leading-[1.3] tracking-[-0.022em] text-white md:text-[28px]">
                {t('faq.title')}
              </h2>
              <p className="text-[13px] leading-[1.4] text-white/60 md:hidden">{t('faq.contactUs.description')}</p>
            </div>
          </div>

          {/* Mobile: 2x2 contact grid + hours */}
          <div className="flex flex-col gap-4 md:hidden">
            <div className="grid grid-cols-2 gap-3">
              {CONTACT_CHANNELS.map((channel) => (
                <a
                  key={channel.label}
                  href={channel.href}
                  {...(channel.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  onClick={() =>
                    window.dataLayer?.push({ event: 'click_main', menu: channel.gaLabel, section: 'Need Help' })
                  }
                  className="flex flex-col items-center justify-center gap-2 rounded-[12px] bg-[#3A3535] px-3 py-4 transition-colors hover:bg-[#4A4545]"
                >
                  <div className="flex h-7 w-7 items-center justify-center">{channel.icon}</div>
                  <span className="text-sm font-medium text-white">{channel.label}</span>
                </a>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-x-2 text-[11px] leading-[1.6] text-white/50">
              {t('faq.contactUs.hours')
                .split('<br/>')
                .map((part, i, arr) => (
                  <React.Fragment key={i}>
                    <span className="whitespace-nowrap">{part.trim()}</span>
                    {i < arr.length - 1 && <span aria-hidden>·</span>}
                  </React.Fragment>
                ))}
            </div>
          </div>

          {/* Desktop: original Contact Us + Help Center blocks */}
          <div className="hidden flex-col md:flex md:gap-y-[52px]">
            {/* Contact Us */}
            <div className="flex gap-x-[22px]">
              <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center">
                <CheckIcon />
              </div>
              <div className="flex flex-col gap-y-[14px]">
                <h3 className="text-[22px] font-bold leading-[1.4] tracking-[-0.022em] text-white">
                  {t('faq.contactUs.title')}
                </h3>
                <div className="flex flex-col gap-y-[15px]">
                  <p className="text-[18px] leading-[1.4] tracking-[-0.022em] text-white/65">
                    <Trans i18nKey="home:faq.contactUs.hours" components={{ br: <br /> }} />
                    <span className="mt-2 inline-block">{t('faq.contactUs.description')}</span>
                  </p>
                  <div className="flex items-center gap-x-[10.45px]">
                    <a
                      href="mailto:official@checkmatekorea.com"
                      onClick={() =>
                        window.dataLayer?.push({ event: 'click_main', menu: 'email', section: 'Need Help' })
                      }
                      className="flex h-[39.7px] w-[39.7px] items-center justify-center rounded-lg bg-[#514C4C] hover:bg-[#5f5858]"
                    >
                      <EmailIcon />
                    </a>
                    <a
                      href="http://pf.kakao.com/_xjSxcxgxj"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        window.dataLayer?.push({ event: 'click_main', menu: 'kakao', section: 'Need Help' })
                      }
                      className="flex h-[39.7px] w-[39.7px] items-center justify-center rounded-lg bg-[#514C4C] hover:bg-[#5f5858]"
                    >
                      <KakaoIcon />
                    </a>
                    <a
                      href="https://api.whatsapp.com/send/?phone=821087033395&text&type=phone_number&app_absent=0"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        window.dataLayer?.push({ event: 'click_main', menu: 'whatsapp', section: 'Need Help' })
                      }
                      className="flex h-[39.7px] w-[39.7px] items-center justify-center rounded-lg bg-[#514C4C] hover:bg-[#5f5858]"
                    >
                      <CallIcon />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Help Center */}
            <div className="flex gap-x-[22px]">
              <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center">
                <CheckIcon />
              </div>
              <div className="flex flex-col gap-y-[14px]">
                <h3 className="text-[22px] font-bold leading-[1.4] tracking-[-0.022em] text-white">
                  {t('faq.helpCenter.title')}
                </h3>
                <p className="text-[18px] leading-[1.4] tracking-[-0.022em] text-white/65">
                  <Trans i18nKey="home:faq.helpCenter.description" components={{ br: <br /> }} />{' '}
                  <a
                    href={HELP_CENTER_URL}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      window.dataLayer?.push({ event: 'click_main', menu: 'help_center', section: 'Need Help' })
                    }
                    className="cursor-pointer text-white underline underline-offset-2"
                  >
                    {t('faq.helpCenter.link')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - FAQ Accordion (desktop only) */}
        <div className="hidden flex-col gap-y-[28px] pt-0 md:flex md:pt-[236px]">
          {/* Tab Buttons */}
          <div className="flex flex-wrap gap-2 md:gap-x-[10px]">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  window.dataLayer?.push({ event: 'click_main', menu: tab.gaLabel, section: 'Need Help' });
                  setActiveTab(tab.id);
                }}
                className={`rounded-[8px] px-3 py-2 text-sm font-semibold leading-[1.5] tracking-[-0.022em] md:px-[18px] md:py-[8px] md:text-[18px] ${
                  activeTab === tab.id ? 'bg-primary text-white' : 'bg-[#464141] text-[#C7C6C6] hover:bg-[#5a5454]'
                }`}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <HomeFAQ activeTab={activeTab} />
        </div>

        {/* Mobile FAQ link button */}
        <button
          onClick={() => {
            window.dataLayer?.push({ event: 'click_main', menu: 'view_all_faqs', section: 'Need Help' });
            navigateWithLang('/guide#faq');
          }}
          className="flex w-full items-center justify-center rounded-[10px] border border-white/30 px-5 py-4 text-sm font-semibold leading-[1.5] tracking-[-0.022em] text-white hover:bg-white/5 md:hidden"
        >
          {t('faq.viewAllFaqs')}
        </button>
      </div>
    </section>
  );
}

export { FaqSection };
